/** The scopes k. needs to read the library through know-mcp. */
export const LIBRARY_SCOPES = ['documents:read', 'sections:read'] as const;

const MCP_PREFIX = 'mcp:';

export interface GrantSource {
  /** The granted scope string from the token response, as oidc-client-ts keeps it. */
  scope?: string | null;
  access_token?: string | null;
}

const split = (value: string): string[] => value.split(/\s+/).filter(Boolean);

function claimScopes(claims: Record<string, unknown>): string[] | null {
  for (const name of ['scope', 'scp']) {
    const value = claims[name];
    if (typeof value === 'string' && value.trim()) return split(value);
    if (Array.isArray(value)) {
      const scopes = value.filter((s): s is string => typeof s === 'string' && s.length > 0);
      if (scopes.length > 0) return scopes;
    }
  }
  return null;
}

function accessTokenClaims(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims: unknown = JSON.parse(window.atob(padded));
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * What the session was granted: the token response's scope, else the access
 * token's `scope` or `scp` claim. Null when neither says, which is not the
 * same as an empty grant.
 */
export function grantedScopes(source: GrantSource): Set<string> | null {
  if (source.scope?.trim()) return new Set(split(source.scope));
  const claims = source.access_token ? accessTokenClaims(source.access_token) : null;
  const fromToken = claims ? claimScopes(claims) : null;
  return fromToken ? new Set(fromToken) : null;
}

/** know-mcp accepts a scope bare or with its `mcp:` prefix. */
export const hasScope = (granted: ReadonlySet<string>, scope: string): boolean =>
  granted.has(scope) || granted.has(MCP_PREFIX + scope);

export function missingLibraryScopes(granted: ReadonlySet<string> | null): string[] {
  if (!granted) return [];
  return LIBRARY_SCOPES.filter((scope) => !hasScope(granted, scope));
}

/**
 * A grant from before the vocabulary rename keeps its old scope names through
 * every refresh, and know-mcp then hides the library from k. Only a fresh
 * sign-in replaces it. A session that says nothing about its scopes is not
 * flagged; k. reports that case itself when a turn finds no library tools.
 */
export const needsFreshSignIn = (source: GrantSource): boolean => missingLibraryScopes(grantedScopes(source)).length > 0;

/**
 * When this grant's sign-in happened, in Unix ms, by the identity host's clock:
 * the access token's `auth_time`, else its `iat`. Null when it says neither.
 */
export function grantSignedInAt(source: GrantSource): number | null {
  const claims = source.access_token ? accessTokenClaims(source.access_token) : null;
  const seconds = [claims?.auth_time, claims?.iat].find((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return seconds === undefined ? null : seconds * 1000;
}
